/**
 * Route helper components
 *
 * Utility components used throughout the routing system:
 * - DashboardSelector: Main dashboard selection UI
 * - DashboardList: List of available dashboards
 * - DashboardSelectorContent: Environment controls display
 */

import React from 'react';
import { getAvailableRoutes } from '../config/featureFlags';

/**
 * DashboardSelector - Main dashboard selector UI component
 *
 * Provides a simple interface for developers to select which dashboard to view.
 * Used on the /selector route for UI development work.
 *
 * @returns {React.ReactElement} - Dashboard selector UI
 */
export function DashboardSelector() {
  return (
    <div style={{ padding: 40 }}>
      <h1 style={{ fontSize: 32, marginBottom: 24 }}>Dashboard Selector</h1>

      <DashboardList />
      <DashboardSelectorContent />
    </div>
  );
}

/**
 * DashboardList - Renders list of available dashboards with links
 *
 * Production uses a single unified Command Center dashboard.
 * Legacy dashboards are disabled and redirect to /command-center.
 *
 * @returns {React.ReactElement} - List of dashboard links
 */
export function DashboardList() {
  return (
    <ul style={{ fontSize: 20, lineHeight: 2 }}>
      <li>
        <a href="/">Landing Page</a>
      </li>
      <li>
        <a href="/command-center">Command Center (Production Dashboard)</a>
      </li>
    </ul>
  );
}

/**
 * DashboardSelectorContent - Displays environment hints and enabled dashboards
 *
 * Shows which dashboards are currently enabled and provides instructions
 * for enabling additional dashboards via environment variables.
 *
 * @returns {React.ReactElement} - Environment controls display
 */
export function DashboardSelectorContent() {
  const availableRoutes = getAvailableRoutes();

  return (
    <>
      <p style={{ marginTop: 32, color: '#888' }}>
        No authentication or role checks. For UI work only.
      </p>
      <p style={{ marginTop: 16, color: '#666', fontSize: 14 }}>
        Enabled dashboards: {Object.keys(availableRoutes).join(', ')}
      </p>

      {/* Environment hints */}
      <div style={{ marginTop: 20, fontSize: 12, color: '#666' }}>
        <p>
          <strong>Environment Controls:</strong>
        </p>
        <ul style={{ marginTop: 8, paddingLeft: 20 }}>
          <li>
            <code>REACT_APP_ENABLEALL_DASHBOARDS=true</code> - Enable all dashboards
          </li>
        </ul>
      </div>
    </>
  );
}
